import { CloseRounded, InfoRounded, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material'
import { Avatar, Box, Chip, Dialog, DialogContent, DialogTitle, IconButton, MenuItem, Pagination, Paper, Skeleton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Toolbar, Typography } from '@mui/material'
import { useState } from 'react'
import { useDispatch } from 'react-redux'
import ApiClient from '../backend/ApiClient'
import { statusOptions } from '../definitions/Approval'
import { IPagination } from '../definitions/Common'
import { ISettlementSchemaWithApprovals } from '../definitions/SettlementSchema'
import Formatter from '../helpers/Formatter'
import Helper from '../helpers/Helper'
import { useAppSelector } from '../store/Hooks'
import { changeRoute } from '../store/slices/SessionSlice'
import useQuery, { buildQueryKey } from './hooks/useQuery'

interface P {
    contractId: number
}

export default function SettlementTable({ contractId }: P) {
    const dispatch = useDispatch()
    const [pagination, setPagination] = useState<IPagination>({ page: 0, batch: 20 })
    const [filterStatus, setFilterStatus] = useState('all')
    const mode = useAppSelector(state => state.app.mode)

    const { status: settlementsStatus, response: settlementsResponse } = useQuery({
        queryKey: buildQueryKey('settlements', contractId, pagination.page, pagination.batch),
        queryFn: () => ApiClient.getSettlements(pagination, contractId),
        cache: true
    })

    const totalItems = settlementsResponse?.totalItems ?? 0
    const totalPage = Math.floor(totalItems / pagination.batch)
    const lastPageHaveValues = totalItems % pagination.batch !== 0

    const settlements: ISettlementSchemaWithApprovals[] = Helper.hasVal(settlementsResponse?.queryResponse) && settlementsResponse?.queryResponse.length > 0
        ? settlementsResponse.queryResponse
        : []

    const filteredSettlements = filterStatus === 'all'
        ? settlements
        : settlements.filter(s => {
            const status = s?.approval?.status ?? 'draft'
            return status === filterStatus
        })

    const grouped: Record<string, ISettlementSchemaWithApprovals[]> = {}
    for (const s of filteredSettlements) {
        if (!grouped[s.period]) {
            grouped[s.period] = []
        }
        grouped[s.period].push(s)
    }

    const handleRowClick = (settlement: ISettlementSchemaWithApprovals) => {
        dispatch(
            changeRoute({
                route: 'settlement-details',
                params: {
                    settlementId: settlement.uuid,
                    contractId,
                    eeId: settlement.executionEnvironmentId
                }
            })
        )
    }

    return (
        <Stack sx={{ overflow: 'hidden', maxHeight: '75vh' }}>
            <Toolbar>
                <Box>
                    <TextField
                        select
                        size='small'
                        label='Status'
                        value={filterStatus}
                        onChange={e => setFilterStatus(e.target.value)}
                        sx={{ minWidth: 160 }}>
                        <MenuItem value='all'>All</MenuItem>
                        {statusOptions.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                            </MenuItem>
                        ))}
                    </TextField>
                </Box>
            </Toolbar>
            <TableContainer sx={{ marginTop: 2 }} component={Paper}>
                <Table stickyHeader>
                    <TableHead sx={{ backgroundColor: mode === 'light' ? 'rgba(0, 0, 0, 0.87)' : undefined }}>
                        <TableRow>
                            <TableCell />
                            <TableCell>Period</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Last Run Time</TableCell>
                            <TableCell>Last Run By</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Info</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody sx={{ '& > :last-child td, & > :last-child th': { border: 0 } }}>
                        {settlementsStatus === 'loading'
                            ? (
                                    Array.from({ length: 5 }).map((_, idx) => (
                                        <TableRow key={idx}>
                                            <TableCell>
                                                <Skeleton variant='circular' width={24} height={24} />
                                            </TableCell>
                                            <TableCell>
                                                <Skeleton width={80} />
                                            </TableCell>
                                            <TableCell>
                                                <Skeleton width={60} />
                                            </TableCell>
                                            <TableCell>
                                                <Skeleton width={120} />
                                            </TableCell>
                                            <TableCell>
                                                <Skeleton width={100} />
                                            </TableCell>
                                            <TableCell>
                                                <Skeleton width={60} />
                                            </TableCell>
                                            <TableCell>
                                                <Skeleton width={60} />
                                            </TableCell>
                                            <TableCell>
                                                <Skeleton width={50} />
                                            </TableCell>
                                            <TableCell>
                                                <Skeleton width={90} />
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )
                            : filteredSettlements.length === 0
                                ? (
                                        <TableRow>
                                            <TableCell colSpan={9} align='center'>
                                                No data available
                                            </TableCell>
                                        </TableRow>
                                    )
                                : (
                                        Object.entries(grouped).map(([period, group]) => (
                                            <ExpandableRow
                                                key={period}
                                                period={period}
                                                settlements={group}
                                                onRowClick={handleRowClick}
                                            />
                                        ))
                                    )}
                    </TableBody>
                </Table>
            </TableContainer>
            <Box display='flex' justifyContent='flex-end' mt={2}>
                <Pagination
                    count={totalPage + (lastPageHaveValues ? 1 : 0)}
                    page={pagination.page + 1}
                    color='primary'
                    disabled={settlementsStatus !== 'success'}
                    onChange={(_, value) =>
                        setPagination(p => ({ batch: p.batch, page: value - 1 }))}
                />
            </Box>
        </Stack>
    )
}

interface ExpandableRowP {
    period: string
    settlements: ISettlementSchemaWithApprovals[]
    onRowClick: (settlement: ISettlementSchemaWithApprovals) => void
}

const ExpandableRow = ({ period, settlements, onRowClick }: ExpandableRowP) => {
    const [open, setOpen] = useState(false)
    const [first, ...rest] = settlements
    const [modalOpen, setModalOpen] = useState(false)
    const [selectedSettlement, setSelectedSettlement] = useState<ISettlementSchemaWithApprovals | null>(null)

    const mode = useAppSelector(state => state.app.mode)

    const handleCloseModal = () => setModalOpen(false)

    return (
        <>
            <TableRow
                hover
                onClick={() => onRowClick(first)}
                sx={{
                    cursor: 'pointer',
                    ...(open && {
                        bgcolor: mode === 'dark' ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.12)'
                    })
                }}>
                <TableCell width={1}>
                    <IconButton
                        sx={{ visibility: rest.length > 0 ? 'visible' : 'hidden' }}
                        size='small'
                        onClick={e => {
                            e.stopPropagation()
                            setOpen(!open)
                        }}>
                        {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                    </IconButton>
                </TableCell>
                <TableCell>{period}</TableCell>
                <TableCell>
                    <Chip color={first.type === 'interim' ? 'default' : 'info'} label={first.type} size='small' sx={{ fontWeight: 600 }} />
                </TableCell>
                <TableCell>{Formatter.date(new Date(first.runAt), 'mm/dd/yyyy hh:mm:ss')}</TableCell>
                <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                        <Avatar sx={{ width: 24, height: 24, fontSize: 14 }}>{first?.userName?.charAt(0)?.toUpperCase()}</Avatar>
                        {first.userName}
                    </Box>
                </TableCell>
                <TableCell>
                    <Chip
                        sx={{
                            borderRadius: '999px',
                            fontWeight: 600
                        }}
                        label={(
                            <Box display='flex' alignItems='center' gap={1}>
                                <Box
                                    width={8}
                                    height={8}
                                    borderRadius='50%'
                                    bgcolor={
                                        (first.approval?.status === 'approved' && first.versioning.isHead)
                                            ? 'success.main'
                                            : (first.approval?.status === 'approved' && !first.versioning.isHead)
                                                    ? 'grey.500'
                                                    : first.approval?.status === 'ready-for-review'
                                                        ? 'info.main'
                                                        : first.approval?.status === 'rejected'
                                                            ? 'red'
                                                            : 'orange'
                                    }
                                />
                                <Typography variant='body2'>
                                    {statusOptions.find(option => option.value === first.approval?.status)?.label ?? 'Draft'}
                                </Typography>
                            </Box>
                        )}
                        variant='outlined'
                        size='small'
                    />
                </TableCell>
                <TableCell>
                    <IconButton
                        size='small'
                        onClick={e => {
                            e.stopPropagation()
                            setSelectedSettlement(first)
                            setModalOpen(true)
                        }}>
                        <InfoRounded fontSize='small' />
                    </IconButton>
                </TableCell>
            </TableRow>

            {open &&
                rest.map(s => (
                    <TableRow
                        key={s.uuid}
                        hover
                        onClick={() => onRowClick(s)}
                        sx={{
                            cursor: 'pointer', ...(open && {
                                bgcolor: mode === 'dark' ? 'rgba(0,0,0,0.15)' : 'rgba(0,0,0,0.05)'
                            })
                        }}>
                        <TableCell />
                        <TableCell>{s.period}</TableCell>
                        <TableCell>
                            <Chip color={s.type === 'interim' ? 'default' : 'info'} label={s.type} size='small' sx={{ fontWeight: 600 }} />
                        </TableCell>
                        <TableCell>{Formatter.date(new Date(s.runAt), 'mm/dd/yyyy hh:mm:ss')}</TableCell>
                        <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                                <Avatar sx={{ width: 24, height: 24, fontSize: 14 }}>{s?.userName?.charAt(0)?.toUpperCase()}</Avatar>
                                {s.userName}
                            </Box>
                        </TableCell>
                        <TableCell>
                            <Chip
                                sx={{
                                    borderRadius: '999px',
                                    fontWeight: 600
                                }}
                                label={(
                                    <Box display='flex' alignItems='center' gap={1}>
                                        <Box
                                            width={8}
                                            height={8}
                                            borderRadius='50%'
                                            bgcolor={
                                                (s.approval?.status === 'approved' && s.versioning.isHead)
                                                    ? 'success.main'
                                                    : (s.approval?.status === 'approved' && !s.versioning.isHead)
                                                            ? 'grey.500'
                                                            : s.approval?.status === 'ready-for-review'
                                                                ? 'info.main'
                                                                : s.approval?.status === 'rejected'
                                                                    ? 'red'
                                                                    : 'orange'
                                            }
                                        />
                                        <Typography variant='body2'>
                                            {statusOptions.find(option => option.value === s.approval?.status)?.label ?? 'Draft'}
                                        </Typography>
                                    </Box>
                                )}
                                variant='outlined'
                                size='small'
                            />
                        </TableCell>
                        <TableCell>
                            <IconButton
                                size='small'
                                onClick={e => {
                                    e.stopPropagation()
                                    setSelectedSettlement(s)
                                    setModalOpen(true)
                                }}>
                                <InfoRounded fontSize='small' />
                            </IconButton>
                        </TableCell>
                    </TableRow>
                ))}

            <Dialog open={modalOpen} onClose={handleCloseModal} PaperProps={{ sx: { width: 500, maxHeight: 400 } }}>
                <DialogTitle sx={{ mb: 1 }}>
                    <Stack flexDirection='row' alignItems='center' justifyContent='space-between'>
                        <Typography variant='h6'>Settlement Info</Typography>
                        <IconButton size='small' onClick={handleCloseModal}>
                            <CloseRounded fontSize='small' />
                        </IconButton>
                    </Stack>
                </DialogTitle>
                <DialogContent>
                    {selectedSettlement && (
                        <Stack flexDirection='row'>
                            <Stack spacing={1} flex={1} justifyContent='center'>
                                <Typography variant='body2' color={theme => theme.palette.text.secondary}>Contract</Typography>
                                <Typography variant='body2'>Version {selectedSettlement.contractVersion}</Typography>
                                <Typography variant='body2' color={theme => theme.palette.text.secondary}>Engine</Typography>
                                <Typography variant='body2'>Version {selectedSettlement.flowVersion}</Typography>
                                <Typography variant='body2' color={theme => theme.palette.text.secondary}>Settlement</Typography>
                                <Typography variant='body2'>Version {selectedSettlement.versioning.version}</Typography>
                            </Stack>
                            <Stack spacing={1} flex={1} justifyContent='center'>
                                <Typography variant='body2' color={theme => theme.palette.text.secondary}>Status:</Typography>
                                <Box>
                                    <Chip
                                        sx={{
                                            borderRadius: '999px',
                                            fontWeight: 600,
                                            height: 'auto'
                                        }}
                                        label={(
                                            <Box display='flex' alignItems='center' gap={1}>
                                                <Box
                                                    width={8}
                                                    height={8}
                                                    borderRadius='50%'
                                                    bgcolor={
                                                        (selectedSettlement.approval?.status === 'approved' && selectedSettlement.versioning.isHead)
                                                            ? 'success.main'
                                                            : (selectedSettlement.approval?.status === 'approved' && !selectedSettlement.versioning.isHead)
                                                                    ? 'grey.500'
                                                                    : selectedSettlement.approval?.status === 'ready-for-review'
                                                                        ? 'info.main'
                                                                        : selectedSettlement.approval?.status === 'rejected'
                                                                            ? 'red'
                                                                            : 'orange'
                                                    }
                                                />
                                                <Typography variant='body2'>
                                                    {statusOptions.find(option => option.value === selectedSettlement.approval?.status)?.label ?? 'Draft'}
                                                </Typography>
                                            </Box>
                                        )}
                                        variant='outlined'
                                        size='small'
                                    />
                                </Box>
                                <Typography variant='body2' color={theme => theme.palette.text.secondary}>Requested by:</Typography>
                                <Stack flexDirection='row' gap={1} alignItems='center'>
                                    <Avatar sx={{ width: 24, height: 24, fontSize: 14 }}>{(selectedSettlement.approval?.changes?.at(-2)?.changedBy ?? selectedSettlement.userName)?.charAt(0)?.toUpperCase()}</Avatar>
                                    <Typography variant='body2'>{selectedSettlement.approval?.changes?.at(-2)?.changedBy ?? selectedSettlement.userName}</Typography>
                                </Stack>
                                <Typography variant='body2' color={theme => theme.palette.text.secondary}>Approved by:</Typography>
                                <Stack flexDirection='row' gap={1} alignItems='center'>
                                    {selectedSettlement.approval?.status === 'approved' && selectedSettlement.approval?.changes?.at(-1)
                                        ? <Avatar sx={{ width: 24, height: 24, fontSize: 14 }}>{selectedSettlement.approval?.changes?.at(-1)?.changedBy?.charAt(0)?.toUpperCase()}</Avatar>
                                        : null}
                                    <Typography variant='body2'>{selectedSettlement.approval?.status === 'approved' && selectedSettlement.approval?.changes?.at(-1)?.changedBy
                                        ? selectedSettlement.approval?.changes?.at(-1)?.changedBy
                                        : '-'}
                                    </Typography>
                                </Stack>
                            </Stack>
                        </Stack>
                    )}
                </DialogContent>
            </Dialog>
        </>
    )
}
